import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { useApp } from '@/components/AppLayout';
import {
  reportsApi,
  type PaymentsReceivedReport,
  type PaymentsReceivedRow,
  type ReportExportFormat,
} from '@/lib/api';
import { formatAmount } from '@/lib/currency';
import { DataTable, SortableHeader } from '@/components/ui/data-table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  ReportLayout,
  SummaryCard,
  SummaryCardsGrid,
  ExportDropdown,
  TimeRangePicker,
  createTimeRange,
  type TimeRange,
} from '@/components/reports';
import type { ColumnDef } from '@tanstack/react-table';
import { Banknote } from 'lucide-react';

export default function PaymentsReceived() {
  const { currentBusiness } = useApp();
  const [report, setReport] = useState<PaymentsReceivedReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<TimeRange>(() => createTimeRange('this_month'));

  useEffect(() => {
    if (currentBusiness) {
      loadReport();
    }
  }, [currentBusiness, timeRange]);

  const loadReport = async () => {
    if (!currentBusiness) return;
    setLoading(true);
    try {
      const response = await reportsApi.getPaymentsReceived(
        currentBusiness.id,
        timeRange.preset === 'custom' ? 'custom' : timeRange.preset,
        timeRange.preset === 'custom' ? format(timeRange.startDate, 'yyyy-MM-dd') : undefined,
        timeRange.preset === 'custom' ? format(timeRange.endDate, 'yyyy-MM-dd') : undefined
      );
      if (response.data) {
        setReport(response.data);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (exportFormat: ReportExportFormat) => {
    if (!currentBusiness) return;
    await reportsApi.exportPaymentsReceived(
      currentBusiness.id,
      exportFormat,
      timeRange.preset === 'custom' ? 'custom' : timeRange.preset,
      timeRange.preset === 'custom' ? format(timeRange.startDate, 'yyyy-MM-dd') : undefined,
      timeRange.preset === 'custom' ? format(timeRange.endDate, 'yyyy-MM-dd') : undefined
    );
  };

  const columns: ColumnDef<PaymentsReceivedRow>[] = [
    {
      accessorKey: 'payment_date',
      header: ({ column }) => <SortableHeader column={column} title="Date" />,
      cell: ({ row }) => {
        const dateStr = row.getValue('payment_date') as string;
        return format(new Date(dateStr), 'MMM d, yyyy');
      },
    },
    {
      accessorKey: 'client_name',
      header: ({ column }) => <SortableHeader column={column} title="Client" />,
    },
    {
      accessorKey: 'payment_method_display',
      header: 'Method',
    },
    {
      accessorKey: 'reference',
      header: 'Reference',
      cell: ({ row }) => {
        const ref = row.getValue('reference') as string;
        return ref || '-';
      },
    },
    {
      accessorKey: 'amount',
      header: ({ column }) => (
        <div className="flex justify-end">
          <SortableHeader column={column} title="Amount" />
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-right font-medium">
          {formatAmount(row.getValue('amount'))}
        </div>
      ),
    },
    {
      accessorKey: 'applied_to_str',
      header: 'Applied To',
      cell: ({ row }) => {
        const applied = row.getValue('applied_to_str') as string;
        return (
          <span className={applied === 'Unapplied' ? 'text-muted-foreground' : ''}>
            {applied}
          </span>
        );
      },
    },
  ];

  return (
    <ReportLayout
      title="Payments Received"
      actions={<ExportDropdown onExport={handleExport} disabled={loading || !report} />}
      filters={
        <TimeRangePicker
          value={timeRange}
          onChange={setTimeRange}
        />
      }
    >
      {report && (
        <>
          <div className="mb-6">
            <SummaryCardsGrid>
              <SummaryCard
                label="Total Received"
                value={formatAmount(report.summary.total_received)}
                variant="success"
              />
              <SummaryCard
                label="Payment Count"
                value={report.summary.payment_count.toString()}
              />
              <SummaryCard
                label="Average Payment"
                value={formatAmount(report.summary.avg_payment)}
              />
            </SummaryCardsGrid>
          </div>

          {report.summary.by_method.length > 0 && (
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-base">By Payment Method</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {report.summary.by_method.map((method) => (
                    <div
                      key={method.method}
                      className="flex items-center justify-between py-2 border-b last:border-0"
                    >
                      <span className="font-medium">{method.display}</span>
                      <div className="text-right">
                        <span className="font-medium">{formatAmount(method.amount)}</span>
                        <span className="text-muted-foreground text-sm ml-2">
                          ({method.count} payments, {method.percent}%)
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
      <DataTable
        columns={columns}
        data={report?.data ?? []}
        loading={loading}
        emptyIcon={<Banknote className="h-6 w-6 text-muted-foreground" />}
        emptyTitle="No Payments"
        emptyDescription="No payments received in this period."
      />
    </ReportLayout>
  );
}
